import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Clock, MessageCircle, Moon, Search, Store } from "lucide-react";
import { api, fileUrl, trackClick } from "../lib/api";
import { usePolling } from "../hooks/usePolling";
import { ProductCard } from "../components/public/ProductCard";
import { ProductModal } from "../components/public/ProductModal";
import { FullLoader } from "../components/ProtectedRoute";
import { Input } from "../components/ui/input";

const waLink = (n) => {
  let d = (n || "").replace(/\D/g, "");
  if (d.startsWith("0")) d = "62" + d.slice(1);
  return `https://wa.me/${d}`;
};

const Message = ({ icon: Icon, title, text, testId }) => (
  <div className="min-h-screen grid place-items-center bg-[#F8FAFC] px-6" data-testid={testId}>
    <div className="text-center max-w-sm fade-up">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-white border border-slate-200"><Icon className="h-6 w-6 text-slate-500" /></div>
      <h1 className="mt-5 text-2xl font-extrabold tracking-tight">{title}</h1>
      <p className="mt-2 text-sm text-slate-600">{text}</p>
      <Link to="/" className="mt-6 inline-block text-sm font-semibold text-slate-900 hover:underline">Buat toko di Jufi</Link>
    </div>
  </div>
);

const Marquee = ({ text }) => (
  <div className="sticky top-0 z-30 bg-slate-900 text-white overflow-hidden whitespace-nowrap py-2 text-sm font-medium" data-testid="public-marquee">
    <div className="inline-flex marquee-track">
      {[0, 1].map((k) => <span key={k} className="min-w-[100vw] shrink-0 px-8">{text}</span>)}
    </div>
  </div>
);

const ShopProfile = ({ shop }) => (
  <div>
    <div className="h-40 sm:h-56 lg:h-64 bg-slate-200 overflow-hidden" data-testid="public-shop-header">
      {shop.header_photo && <img src={fileUrl(shop.header_photo)} alt="" className="h-full w-full object-cover" />}
    </div>
    <div className="mx-auto max-w-6xl px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div className={`-mt-12 sm:-mt-14 relative z-10 h-24 w-24 sm:h-28 sm:w-28 shrink-0 overflow-hidden rounded-full border-4 bg-slate-900 grid place-items-center text-3xl font-bold text-white ring-2 ${shop.is_open ? "ring-emerald-400" : "ring-slate-300"} border-white`}>
          {shop.profile_photo ? <img src={fileUrl(shop.profile_photo)} alt="" className="h-full w-full object-cover" data-testid="public-shop-avatar" /> : shop.name[0]}
        </div>
        <div className="flex-1 min-w-0 sm:pt-4">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="public-shop-name">{shop.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {shop.category && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700" data-testid="public-shop-category">{shop.category}</span>}
            <span className={`rounded-full px-2.5 py-1 font-semibold ${shop.is_open ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`} data-testid="public-shop-open-badge">
              {shop.is_open ? "Buka" : "Tutup"}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-slate-600"><Clock className="h-3 w-3" />{shop.open_time} – {shop.close_time}</span>
          </div>
        </div>
        {shop.whatsapp && (
          <a href={waLink(shop.whatsapp)} target="_blank" rel="noreferrer" data-testid="public-shop-whatsapp-btn"
            className="sm:mb-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white hover:brightness-105 transition">
            <MessageCircle className="h-4 w-4" />Chat WhatsApp
          </a>
        )}
      </div>
      {shop.description && <p className="mt-4 max-w-2xl text-sm text-slate-600 whitespace-pre-line" data-testid="public-shop-description">{shop.description}</p>}
    </div>
  </div>
);

const Catalog = ({ products, onOpen, whatsapp }) => {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const cats = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const list = products.filter((p) => (!cat || p.category === cat) && p.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10" data-testid="public-catalog">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between mb-6">
        <h2 className="text-lg font-bold">Katalog produk <span className="text-slate-400 font-medium">({products.length})</span></h2>
        <div className="relative sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk..." className="pl-9 rounded-full bg-white" data-testid="public-search-input" />
        </div>
      </div>
      {cats.length > 1 && (
        <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
          {["", ...cats].map((c) => (
            <button key={c || "all"} onClick={() => setCat(c)} data-testid={`public-category-${c || "all"}`}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${cat === c ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
              {c || "Semua"}
            </button>
          ))}
        </div>
      )}
      {!list.length && <p className="py-16 text-center text-sm text-slate-500" data-testid="public-catalog-empty">Belum ada produk.</p>}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
        {list.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} onOpen={onOpen} whatsapp={whatsapp} />)}
      </div>
    </section>
  );
};

export default function PublicShop() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const openId = params.get("p");

  usePolling(async () => {
    try {
      const { data: d } = await api.get(`/public/shops/${slug}`);
      setData(d);
      setNotFound(false);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
    }
  }, 8000, [slug]);

  const viewed = useRef(new Set());
  useEffect(() => {
    if (openId && !viewed.current.has(openId)) {
      viewed.current.add(openId);
      trackClick(openId, "view");
    }
  }, [openId]);

  useEffect(() => {
    if (data?.shop?.name) document.title = `${data.shop.name} · Jufi`;
  }, [data?.shop?.name]);

  if (notFound) return <Message icon={Store} title="Toko tidak ditemukan" text={`Tidak ada toko dengan link /${slug}.`} testId="public-shop-not-found" />;
  if (!data) return <FullLoader />;
  if (!data.available) return <Message icon={Store} title="Toko tidak tersedia" text="Toko ini sedang tidak aktif untuk publik." testId="public-shop-unavailable" />;

  const { shop, products } = data;
  const selected = products.find((p) => p.product_id === openId) || null;
  const open = (p) => setParams({ p: p.product_id });
  const close = () => setParams({});

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="public-shop-page">
      {shop.breaking_news && <Marquee text={shop.breaking_news} />}
      <ShopProfile shop={shop} />
      {shop.is_open ? (
        <Catalog products={products} onOpen={open} whatsapp={shop.whatsapp} />
      ) : (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center fade-up" data-testid="public-shop-closed">
            <Moon className="mx-auto h-8 w-8 text-slate-400" />
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight">Toko Sedang Tutup</h2>
            <p className="mt-2 text-sm text-slate-600">Jam operasional {shop.open_time} – {shop.close_time}. Silakan kembali lagi nanti.</p>
          </div>
        </div>
      )}
      <footer className="py-10 text-center text-xs text-slate-400">Dibuat dengan <Link to="/" className="font-semibold text-slate-600 hover:underline">jufi.</Link></footer>
      <ProductModal product={selected} slug={shop.slug} whatsapp={shop.whatsapp} onClose={close} />
    </div>
  );
}
